import React, { useEffect, useRef, useState } from 'react'

import { patchApplication, postApplication } from 'api/application'
import getActiveLoans from 'api/application/getAllActiveLoans'
import getFinalDecision from 'api/application/getFinalDecision'
import postApplicationCollateralsAssets from 'api/application/postApplicationCollateralsAssets'
import { generateRvCollaterals } from 'api/application/postRvApplicationCollateralsAssets'
import { isUnauthorizedState } from 'api/auth/unauthorizedInterceptor'
import { Disclosure } from 'api/lender/getLenderDisclosures'
import { AxiosError } from 'axios'
import SecondCheckpointModal from 'components/CheckpointModal/SecondCheckpointModal'
import NewApplicationFormBetaNotice from 'components/notices/NewApplicationFormBetaNotice'
import RoadrunnerDealershipAgreementNotice from 'components/notices/RoadrunnerDealershipAgreementNotice'
import { Titlebar } from 'containers/ApplicationSubmissionForms/Titlebar'
import { FormValues } from 'containers/FormBuilder'
import ReturnCustomerModal from 'containers/ReturnCustomer/ReturnCustomerModal'
import {
    ApplicationDataType,
    ActiveLoanType,
} from 'containers/ReturnCustomer/types/returnCustomerTypes'
import { useFeatureFlagContext } from 'contexts/FeatureFlagContextProvider'
import { useLender } from 'contexts/LenderContextProvider'
import useLenderDisclosures from 'hooks/useLenderDisclosures'
import useUser from 'hooks/useUser'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'
import LoadingPage from 'pages/LoadingPage/LoadingPage'
import DisclosureType from 'state/enums/DisclosureType'
import SourceClient from 'state/enums/SourceClient'
import SourceSubclient from 'state/enums/SourceSubclient'
import { handleValidationError } from 'utils/formatValidationErrors'
import getApplicationDecisionPath from 'utils/getApplicationDecisionPath'
import * as toast from 'utils/toast'

import ApplicationFormBuilder, {
    ApplicationFormBuilderProps,
} from '../ApplicationFormBuilder'
import { MainContainer } from '../ApplicationFormBuilder.styled'
import createPostApplicationDTO from '../AutomobileApplication/createPostApplicationDTO'
import DeveloperFormDataQuickFill from '../DeveloperFormDataQuickFill/DeveloperFormDataQuickFill'
import {
    DISCLOSURE_LOAD_ERROR_MESSAGE,
    hasRequiredDisclosures,
} from '../disclosureUtils'
import {
    COAPPLICANT_TAG,
    isApplicationJointFunc,
    isCoapplicantAddressSameFunc,
    applicantCitizenshipStatusLinkingFunc,
    coapplicantCitizenshipStatusLinkingFunc,
} from '../sharedInstructions'
import { swapApplicantsFunc } from '../utils'
import { rvFormInstructions } from './rvApplicationFields'

type RVApplicationFormProps = {
    overrideDefaultInitialValues?: ApplicationFormBuilderProps['overrideDefaultInitialValues']
    isLoading?: boolean
    isCopy?: boolean
    parentId?: number
    parentUuid?: string
    crossoverApplication?: boolean
    isReadOnly?: boolean
}

const submitApplication = async (
    uuid: string,
    values: FormValues,
    setShowSubmissionModal: (state: boolean) => void,
    setShowReturnCustomerModal: (state: boolean) => void,
    setApplicationData: (state: ApplicationDataType) => void,
    setActiveLoans: (state: ActiveLoanType[]) => void,
    returnCustomerPsApp2FeatureFlag: boolean | undefined,
    useLegacyDecisionPage: boolean | undefined,
    finalize: boolean,
    isCopy?: boolean,
    parentId?: number,
    disclosures?: Disclosure[],
): Promise<string> => {
    let redirectUrl
    let targetUuid: string = uuid

    const tryShowReturnCustomerModal = async (
        applicationData: ApplicationDataType,
    ) => {
        const activeLoans = await getActiveLoans({
            id: applicationData.id,
            application_type: applicationData.application_type,
            social_security_number: applicationData.social_security_number,
            coapplicant_social_security_number:
                applicationData.coapplicant_social_security_number,
            parent_id: parentId,
        })

        if (activeLoans?.length) {
            setActiveLoans(activeLoans)
            setApplicationData(applicationData)
            setShowReturnCustomerModal(true)

            return true
        }

        return false
    }

    try {
        const applicationDTO = createPostApplicationDTO(
            values,
            isCopy,
            parentId,
            SourceClient.OCTANE_EXTENSION,
            SourceSubclient.RV_APP_2,
            disclosures,
        )

        let applicationData

        if (targetUuid) {
            applicationData = await patchApplication(applicationDTO, targetUuid)
        } else {
            applicationData = await postApplication(applicationDTO)
            targetUuid = applicationData.uuid
        }

        const collaterals = await generateRvCollaterals(
            applicationData.id,
            values.vehicle,
        )

        await postApplicationCollateralsAssets(targetUuid, collaterals)

        if (finalize) {
            if (returnCustomerPsApp2FeatureFlag) {
                if (await tryShowReturnCustomerModal(applicationData)) {
                    return targetUuid
                }
            }

            setShowSubmissionModal(true)
            await getFinalDecision(targetUuid)

            redirectUrl = getApplicationDecisionPath(
                applicationData.id,
                useLegacyDecisionPage,
            )
        } else {
            toast.success({
                message: `Application saved`,
                withCloseIcon: false,
            })
        }
    } catch (error) {
        setShowSubmissionModal(false)
        setShowReturnCustomerModal(false)

        if (!isUnauthorizedState()) {
            OctaneLogger.axiosError(
                'Unexpected error when trying to submit an application.',
                error as AxiosError,
                {
                    applicationUuid: uuid,
                    isCopy,
                    parentId,
                },
            )

            const axiosError = error as AxiosError

            const validationErrors = axiosError.response?.data as
                | Record<string, string[]>
                | undefined

            const isValidationError =
                axiosError.response?.status === 400 && validationErrors

            if (isValidationError) {
                handleValidationError(validationErrors, 'RV form')
            } else {
                redirectUrl = `#/saved-applications`

                toast.error({
                    message: `An unexpected error occurred. Please try again or contact support.`,
                    withCloseIcon: false,
                })
            }
        }
    }

    if (redirectUrl) {
        window.location.hash = redirectUrl
    }

    return targetUuid
}

export default function RVApplicationForm({
    overrideDefaultInitialValues,
    isLoading,
    isCopy,
    parentId,
    parentUuid,
    crossoverApplication,
    isReadOnly,
}: RVApplicationFormProps) {
    const [uuid, setUuid] = useState('')
    const [showSubmissionModal, setShowSubmissionModal] = useState(false)
    const finalizeRef = useRef(false)
    const [activeLoans, setActiveLoans] = useState<ActiveLoanType[]>([])

    const [
        isAgreementSignedWithRoadrunner,
        setIsAgreementSignedWithRoadrunner,
    ] = useState<boolean>()

    const [developerOverrideData, setDeveloperOverrideData] =
        useState<FormValues>()

    const dealershipLenderAgreementValidation =
        useFeatureFlagContext()?.dealershipLenderAgreementValidation

    const { user } = useUser()
    const { id: lenderId } = useLender()

    const { disclosures, loading, retryAttemptsExhausted } =
        useLenderDisclosures(lenderId)

    useEffect(() => {
        setIsAgreementSignedWithRoadrunner(
            user?.dealership?.isAgreementSignedWithRoadrunner,
        )
    }, [user?.dealership?.isAgreementSignedWithRoadrunner])

    const [applicationData, setApplicationData] = useState<
        ApplicationDataType | undefined
    >()

    const [showReturnCustomerModal, setShowReturnCustomerModal] =
        useState(false)

    const featureFlags = useFeatureFlagContext()
    const returnCustomerPsApp2FeatureFlag = featureFlags?.returnCustomerPsApp2
    const useLegacyDecisionPage = !featureFlags?.modernizedRvDecisionPage

    if (isLoading) {
        return <LoadingPage />
    }

    const disclosuresLoaded = hasRequiredDisclosures(disclosures, [
        DisclosureType.RV_APPLICATION_FORM_DISCLOSURE,
    ])

    const disclosureLoadFailed = !loading && retryAttemptsExhausted

    const canSubmit =
        !(
            dealershipLenderAgreementValidation === true &&
            isAgreementSignedWithRoadrunner === false
        ) &&
        !loading &&
        disclosuresLoaded

    const initialValuesOverride =
        developerOverrideData || overrideDefaultInitialValues

    return (
        <MainContainer crossoverApplication={crossoverApplication}>
            {!isReadOnly && <RoadrunnerDealershipAgreementNotice />}
            {!isReadOnly && disclosureLoadFailed && (
                <p role="alert" data-testid="disclosure-load-error-message">
                    {DISCLOSURE_LOAD_ERROR_MESSAGE}
                </p>
            )}

            <ReturnCustomerModal
                applicationData={applicationData}
                activeLoans={activeLoans}
                isOpen={showReturnCustomerModal}
                handleClose={() => setShowReturnCustomerModal(false)}
            />
            <SecondCheckpointModal isOpen={showSubmissionModal} animated />

            {!isReadOnly && (
                <>
                    <NewApplicationFormBetaNotice />

                    {!crossoverApplication && (
                        <Titlebar
                            title={isCopy ? 'Copy Into New' : 'New Application'}
                        />
                    )}

                    <DeveloperFormDataQuickFill
                        setDeveloperOverrideData={setDeveloperOverrideData}
                    />
                </>
            )}

            <ApplicationFormBuilder
                onSubmit={async (values) => {
                    const newUuid = await submitApplication(
                        uuid,
                        values,
                        setShowSubmissionModal,
                        setShowReturnCustomerModal,
                        setApplicationData,
                        setActiveLoans,
                        returnCustomerPsApp2FeatureFlag,
                        useLegacyDecisionPage,
                        finalizeRef.current,
                        isCopy,
                        parentId,
                        disclosures,
                    )

                    setUuid(newUuid)
                }}
                collateralType="RV"
                instructions={rvFormInstructions}
                coapplicantTag={COAPPLICANT_TAG}
                isApplicationJointFunc={isApplicationJointFunc}
                showCitizenshipStatusDisclaimer={
                    applicantCitizenshipStatusLinkingFunc
                }
                showCoapplicantCitizenshipStatusDisclaimer={
                    coapplicantCitizenshipStatusLinkingFunc
                }
                showConsumerShoppedCollateralNotice={Boolean(
                    isCopy && parentUuid,
                )}
                swapApplicantsFunc={swapApplicantsFunc}
                overrideDefaultInitialValues={{
                    ...initialValuesOverride,
                }}
                enableReinitialize
                isCoapplicantAddressSameFunc={isCoapplicantAddressSameFunc}
                setFinalize={() => {
                    finalizeRef.current = true
                }}
                canSubmit={canSubmit}
                disclosureLoadFailed={disclosureLoadFailed}
                isReadOnly={isReadOnly}
                parentUuid={parentUuid}
            />
        </MainContainer>
    )
}

RVApplicationForm.defaultProps = {
    overrideDefaultInitialValues: undefined,
    isCopy: false,
    isLoading: false,
    parentId: undefined,
    parentUuid: undefined,
    isReadOnly: false,
}

export const testExports = {
    submitApplication,
}
